import React, { createContext, useContext, useState, useEffect } from 'react';
import enhancedApiClient from '../api/enhancedApiClient';

const EventContext = createContext();

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within EventProvider');
  }
  return context;
};

export const EventProvider = ({ children, user }) => {
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [vendorCount, setVendorCount] = useState(0);
  const [guestCount, setGuestCount] = useState(0);

  const [eventId, setEventId] = useState(null);

  // Load eventId from user profile
  useEffect(() => {
    const loadUserEventId = async () => {
      if (!user?.username) return;
      
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${user.username}`);
        const userProfile = await response.json();
        setEventId(userProfile?.currentEventId || null);
      } catch (error) {
        console.error('Failed to load user event:', error);
        setEventId(null);
      }
    };
    loadUserEventId();
  }, [user?.username]);

  const loadEventData = async () => {
    setLoading(true);
    try {
      // Update user's currentEventId to match the event they're viewing
      if (user?.username) {
        await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${user.username}/current-event`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId })
        });
      }
      
      // Try to get real event data from database
      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}`);
      if (!response.ok) {
        throw new Error('Event not found');
      }
      
      const eventItem = await response.json();
      if (!eventItem) {
        setEventData(null);
        setQuotes([]);
        setTasks([]);
        setMessages([]);
        return;
      }
      
      // Convert DynamoDB format to frontend format
      const event = {
        eventId: eventItem.id,
        clientName: eventItem.name,
        eventDate: eventItem.eventDate,
        guestCount: parseInt(eventItem.guestCount),
        budget: parseInt(eventItem.budget),
        eventType: eventItem.eventType,
        status: eventItem.status
      };
      
      setEventData(event);
      
      // Load related data
      const [tasksResponse, guestsResponse, vendorsResponse] = await Promise.all([
        fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/tasks`),
        fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/guests`),
        fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/vendors`)
      ]);
      
      const tasksData = tasksResponse.ok ? await tasksResponse.json() : [];
      const guestsData = guestsResponse.ok ? await guestsResponse.json() : [];
      const vendorsData = vendorsResponse.ok ? await vendorsResponse.json() : [];
      
      // Convert DynamoDB task format to frontend format
      const formattedTasks = tasksData.map(item => ({
        ...item,
        id: item.id,
        name: item.name,
        details: item.details,
        dueDate: item.dueDate,
        status: item.status,
        actionName: item.actionName,
        vendorId: item.vendorId,
        createdByUserId: item.createdByUserId,
        documentUrl: item.documentUrl
      }));
      
      setTasks(formattedTasks || []);
      setQuotes([]); // No quotes in sample data yet
      setMessages([]); // No messages in sample data yet
      setVendorCount(vendorsData?.length || 0);
      setGuestCount(guestsData?.length || 0);
      
      console.log('📊 Event data loaded:', {
        event: event.clientName,
        tasks: tasksData?.length || 0,
        guests: guestsData?.length || 0
      });
      
    } catch (error) {
      console.error('❌ Error loading event data:', error);
      // No fallback data - show null state
      setEventData(null);
      setQuotes([]);
      setTasks([]);
      setMessages([]);
      setVendorCount(0);
      setGuestCount(0);
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async (updates) => {
    try {
      await enhancedApiClient.updateEvent(eventData.eventId, updates);
      setEventData({ ...eventData, ...updates });
      return true;
    } catch (error) {
      console.error('Failed to update event:', error);
      return false;
    }
  };

  const refreshTasks = async () => {
    try {
      const tasksData = await enhancedApiClient.getTasks(eventData?.eventId);
      const formattedTasks = tasksData.map(item => ({
        ...item,
        id: item.id,
        name: item.name,
        details: item.details,
        dueDate: item.dueDate,
        status: item.status,
        actionName: item.actionName,
        vendorId: item.vendorId,
        createdByUserId: item.createdByUserId,
        documentUrl: item.documentUrl
      }));
      setTasks(formattedTasks || []);
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
    }
  };

  useEffect(() => {
    if (user && eventId) {
      loadEventData();
    }
  }, [user, eventId]);

  const value = {
    loading,
    eventData,
    quotes,
    tasks,
    messages,
    vendorCount,
    guestCount,
    updateEvent,
    refreshTasks,
    reload: loadEventData
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};